import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Collapse,
  Divider,
  Chip,
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CircleIcon from '@mui/icons-material/Circle';
import { DashboardService } from '../../../service/dashboard.service';

const LicenseSettingsView: React.FC = () => {
  const [licenseUntil, setLicenseUntil] = useState<Date | null>(null);
  const [accountName, setAccountName] = useState<string>('');
  const [showLicenseDetails, setShowLicenseDetails] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLicenseData();
  }, []);

  const fetchLicenseData = async () => {
    try {
      setLoading(true);
      const data = await DashboardService.getDashboard();
      
      if (data.displayName) {
        setAccountName(data.displayName);
      }
      
      setLicenseUntil(data.licenseUntil ? new Date(data.licenseUntil) : null);
    } catch (error) {
      console.error('Fehler beim Abrufen der Lizenzdaten:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLicenseDetails = () => {
    setShowLicenseDetails(!showLicenseDetails);
  };

  const isLicenseActive = licenseUntil && licenseUntil > new Date();

  const statusText = isLicenseActive
    ? 'Aktiv'
    : licenseUntil
    ? 'Abgelaufen'
    : 'Keine Lizenz';

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <VpnKeyIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h5" component="h1">
          Lizenz-Informationen
        </Typography>
      </Box>

      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ marginBottom: 1 }}>
            Account: {accountName}
          </Typography>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6">Lizenzstatus</Typography>
            <CircleIcon
              sx={{
                fontSize: 16,
                color: isLicenseActive ? 'green' : 'red',
                ml: 2
              }}
            />
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {statusText}
            </Typography>
          </Box>

          {licenseUntil ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" gutterBottom>
                {isLicenseActive 
                  ? `Aktiv bis: ${licenseUntil.toLocaleDateString()}` 
                  : `Abgelaufen am: ${licenseUntil.toLocaleDateString()}`
                }
              </Typography>
            </Box>
          ) : (
            <Typography variant="body1" sx={{ mb: 3 }}>
              Keine aktive Lizenz vorhanden
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {!isLicenseActive && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color={licenseUntil ? "error" : "primary"}
                onClick={handleToggleLicenseDetails}
              >
                {licenseUntil ? "Lizenz verlängern" : "Lizenz anfragen"}
              </Button>
              
              <Collapse in={showLicenseDetails}>
                <Card sx={{ mt: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Kontaktinformationen</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon />
                        <Typography variant="body1">+49 173 4700004</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon />
                        <Typography variant="body1">info@washprotech.de</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Collapse>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LicenseSettingsView; 